import type { IPlatformService } from '~/services/platform/types';

/**
 * BrightnessService — controle de brilho do player.
 *
 * <p><b>Estratégia híbrida (V047)</b>: tenta hardware primeiro via
 * {@code platformService.setHardwareBrightness}; se a plataforma jogar
 * {@code 'unsupported'} (Tizen consumer, webOS, browser, ou Android sem
 * plugin instalado), cai pro overlay CSS fullscreen semi-transparente.
 *
 * <p><b>Brilho de hardware no Android</b>: o plugin
 * {@code @capacitor-community/screen-brightness} já vem instalado
 * automaticamente — o script {@code scripts/ensure-brightness-plugin.sh}
 * (invocado tanto pelo {@code execute.sh} dev quanto pelo
 * {@code build-tenant.sh} de produção) garante a dependência, a
 * permission {@code WRITE_SETTINGS} no AndroidManifest e o
 * {@code npx cap sync android}. Sem intervenção manual.
 *
 * <p><b>Diagnóstico</b>: {@link #getLastSource()} retorna {@code 'hw'} ou
 * {@code 'overlay'} pra você saber se a operação afetou o hardware real
 * ou só simulou via CSS. Exibido no painel de debug do player.
 *
 * <p>Mantém o padrão singleton pra ficar fácil consumer (ConfigManager
 * chama setBrightness/setAuto). Use {@link #init(platformService)} no
 * boot do app pra habilitar o caminho de hardware.
 */
export class BrightnessService {
  private static instance: BrightnessService;
  private static readonly OVERLAY_ID = 'efflux-brightness-overlay';

  private platformService: IPlatformService | null = null;
  private lastSource: 'hw' | 'overlay' | null = null;
  private lastAppliedLevel: number | null = null;

  private constructor() {}

  public static getInstance(): BrightnessService {
    if (!BrightnessService.instance) {
      BrightnessService.instance = new BrightnessService();
    }
    return BrightnessService.instance;
  }

  /**
   * Injeta o platform service pra habilitar o caminho de hardware.
   * Chame uma vez no boot do app (App.tsx ou index.tsx). Idempotente.
   */
  public init(platformService: IPlatformService): void {
    this.platformService = platformService;
  }

  /**
   * Define o brilho em 0-100. Tenta hardware primeiro (via platform
   * service); se a plataforma rejeitar com 'unsupported', cai pro
   * overlay CSS. {@link #getLastSource()} indica qual caminho foi usado.
   */
  public async setBrightness(level: number): Promise<void> {
    const safeLevel = Math.max(0, Math.min(100, Math.round(level)));
    this.lastAppliedLevel = safeLevel;

    // Tenta hardware primeiro
    if (this.platformService) {
      try {
        await this.platformService.setHardwareBrightness(safeLevel);
        console.log(
          `[BrightnessService] Hardware brightness set to ${safeLevel}%`,
        );
        // Removemos qualquer overlay antigo — hardware está controlando agora
        this.removeOverlay();
        this.lastSource = 'hw';
        return;
      } catch (e) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const code = (e as any)?.code;
        if (code !== 'unsupported') {
          console.warn(
            '[BrightnessService] Hardware path failed, falling back to overlay:',
            e,
          );
        }
        // fallthrough → overlay
      }
    }

    // Fallback: overlay CSS
    console.log(
      `[BrightnessService] Overlay brightness set to ${safeLevel}% (hardware unavailable)`,
    );
    if (typeof document === 'undefined') return;
    try {
      const overlay = this.ensureOverlay();
      // 100% brilho → opacity 0; 0% brilho → opacity 0.85 (não 1.0 pra
      // manter algum sinal visível, evitar tela 100% preta confusa)
      const opacity = (1 - safeLevel / 100) * 0.85;
      overlay.style.opacity = opacity.toFixed(3);
      this.lastSource = 'overlay';
    } catch (e) {
      console.error('[BrightnessService] Failed to set overlay brightness:', e);
      this.lastSource = null;
    }
  }

  /**
   * Retorna o brilho EFETIVO aplicado pelo overlay (0-100), ou null se o
   * overlay não estiver ativo (= modo auto ou hardware-controlled).
   * Diagnóstico inverso do {@link #setBrightness}.
   */
  public getOverlayBrightness(): number | null {
    if (typeof document === 'undefined') return null;
    const overlay = document.getElementById(
      BrightnessService.OVERLAY_ID,
    ) as HTMLDivElement | null;
    if (!overlay) return null;
    const opacity = parseFloat(overlay.style.opacity || '0');
    if (Number.isNaN(opacity)) return null;
    // opacity = (1 - level/100) * 0.85 → level = 100 * (1 - opacity/0.85)
    const level = 100 * (1 - opacity / 0.85);
    return Math.max(0, Math.min(100, Math.round(level)));
  }

  /**
   * Indica de onde veio o último setBrightness aplicado. {@code 'hw'} =
   * brilho real do hardware foi alterado. {@code 'overlay'} = só CSS.
   * {@code null} = ainda não houve set (modo auto desde o boot).
   */
  public getLastSource(): 'hw' | 'overlay' | null {
    return this.lastSource;
  }

  /**
   * Retorna o último level (0-100) aplicado, independente da fonte.
   * Útil pra reconciliar com a config quando hw drift acontece.
   */
  public getLastAppliedLevel(): number | null {
    return this.lastAppliedLevel;
  }

  /**
   * Ativa modo automático — remove o overlay e deixa o SO controlar
   * o brilho normalmente. App não interfere mais.
   */
  public setAuto(): void {
    console.log('[BrightnessService] Setting brightness to AUTO (no overlay)');
    this.removeOverlay();
    this.lastSource = null;
    this.lastAppliedLevel = null;
  }

  private removeOverlay(): void {
    if (typeof document === 'undefined') return;
    const existing = document.getElementById(BrightnessService.OVERLAY_ID);
    if (existing) {
      existing.remove();
    }
  }

  /**
   * Cria o overlay se não existir ainda. Idempotente.
   * z-index alto (2147483646 = INT_MAX-1) garante que fica acima de
   * QUALQUER conteúdo do player (videos, alerts, overlays internos).
   * pointer-events: none evita interferir em interações de touch/click.
   */
  private ensureOverlay(): HTMLDivElement {
    let overlay = document.getElementById(
      BrightnessService.OVERLAY_ID,
    ) as HTMLDivElement | null;
    if (overlay) return overlay;

    overlay = document.createElement('div');
    overlay.id = BrightnessService.OVERLAY_ID;
    overlay.style.position = 'fixed';
    overlay.style.top = '0';
    overlay.style.left = '0';
    overlay.style.width = '100vw';
    overlay.style.height = '100vh';
    overlay.style.background = '#000';
    overlay.style.opacity = '0';
    overlay.style.pointerEvents = 'none';
    overlay.style.zIndex = '2147483646';
    overlay.style.transition = 'opacity 200ms ease-out';
    document.body.appendChild(overlay);
    return overlay;
  }
}
